// actions.ts
import { get, map, mapValues } from 'lodash'
import moment from 'moment'
import { getReq, postReq } from '../../utils/request'
import { CREATE_PAYSLICES_USER, LOAD_PAYSLICES_USER, LOAD_PAYSLIPS } from './types'
// eslint-disable-next-line import/no-cycle
import { ThunkResult } from '../index'
import { IResponsePagination } from '../../types/interfaces'
import { IResetStore } from '../generalTypes'
import firebaseAdmin from '../../utils/firebase'
import { COMPLAINT_STATUS } from '../complaints/actions'

export type IPayslipsActions = IResetStore | IGetPayslipsActions | IPayslipActions

export interface IPayslipTableItem {
	id: number
	month: string
	paycheckFromManager: number
	annualCleaningBonus: number
	finalSalary: number
	status: PayslipStatus
	user: {
		id: number
		fullName: string
	}
}

export interface IPayslipOriginalItem {
	id: number
	month: string
	paycheckFromManager: number
	annualCleaningBonus: number
	finalSalary: number
	status: PayslipStatus
	user: {
		id: number
		fullName: string
	}
}

export interface IPayslipsPayload {
	tableData: IPayslipTableItem[]
	originalData: IPayslipOriginalItem[]
	pagination: IResponsePagination | null
}

interface IGetPayslipsActions {
	type: LOAD_PAYSLIPS
	payload: IPayslipsPayload
}

export interface IGetPayslipsParams {
	limit: number | null
	page: number | null
	queryParams: any
}

export const getPayslips = (
	params: IGetPayslipsParams,
	onSuccess: (data: IPayslipsPayload) => void = () => {}
): ThunkResult<Promise<IPayslipsPayload>> => async (dispatch) => {
	let payload = {} as IPayslipsPayload
	try {
		dispatch({ type: LOAD_PAYSLIPS.START })

		const queries = mapValues({
			limit: params.limit,
			page: params.page,
			...params.queryParams
		}, (query) => query || undefined)

		const { data } = await getReq('/api/admin/payslips', queries)

		const originalData: IPayslipOriginalItem[] = get(data, 'payslips', [])
		const tableData: IPayslipTableItem[] = map(originalData, (item) => ({
			id: item.id,
			month: moment(item.month).format('MM/YYYY'),
			paycheckFromManager: item.paycheckFromManager,
			annualCleaningBonus: item.annualCleaningBonus,
			finalSalary: item.finalSalary,
			status: item.status,
			user: item.user
		}))

		payload = {
			tableData,
			originalData,
			pagination: get(data, 'context')
		}

		dispatch({ type: LOAD_PAYSLIPS.DONE, payload })
		onSuccess(payload)
		return payload
	} catch (error) {
		dispatch({ type: LOAD_PAYSLIPS.FAILED })
		return Promise.reject(error)
	}
}

export enum PayslipStatus {
	DRAFT = 'DRAFT',
	SAVED = 'SAVED',
	FINALIZED = 'FINALIZED'
}

export interface IFormattedFinish {
	id: number
	date: string // napr. "17.07.2025"
	frequency: string
	qrScans: {
		from: string | null
		to: string | null
		duration: string | null
	}
	errorMessages: string[]
}

export interface ICalculatedYearFinish extends IFormattedFinish {
	jobId: number
	address: string
	windows: number
	missingWindowsError: boolean
}

export interface IChargedPriceForFlatsItem {
	jobId: number
	address: string
	finishesAllCount: number
	finishesOfUserCount: number
	chargedPriceAll: number
	chargedPriceForThisEmployee: number
	flats: number
	windows: number
	missingWindowsError: boolean
	priceFlatPerMonth: number
	priceFlatPerMonthMissingError: boolean
	missingFlatCountError: boolean
	finishes: IFormattedFinish[]
	yearFinishes: IFormattedFinish[]
	annualCleaningBonus: number
	bonusForYearFinishes: number
	errorQrCodesProblemCount: number
}

export interface IJobFormatted {
	id: number
	address: string
	note?: string
	finishes: any[]
	qrScans: any[]
	createdAt?: string
	updatedAt?: string
	[key: string]: any
}

export interface IComplaintsSummaryItem {
	id: number
	status: COMPLAINT_STATUS
	createdAt: string
	note?: string | null
	job?: { id: number, address: string } | null
	finishID?: number | null
}

export interface IComplaintsSummary {
	total: number
	byStatus: Record<COMPLAINT_STATUS, number>
	items: IComplaintsSummaryItem[]
}

export interface ITelephoneSurveysSummaryItem {
	id: number
	callStatus: string
	satisfaction: string | null
	calledAt: string
	note?: string | null
	job?: { id: number, address: string } | null
}

export interface ITelephoneSurveysSummary {
	total: number
	answeredCount: number
	noAnswerCount: number
	satisfiedCount: number
	unsatisfiedCount: number
	items: ITelephoneSurveysSummaryItem[]
}

export interface IPayslipUserDetail {
	payslipID: number
	user: string
	cleanedFlats: number
	maximalBasePaycheck: number
	paycheckFromManager: number
	finalSalary: number
	allTimeWorkedThisMonth: number
	allTimeWorkedThisMonthFormatted: string
	allMoneyEarned: number
	listOfJobsMissingPrice: IChargedPriceForFlatsItem[]
	chargedPriceForFlats: number
	chargedPriceForFlatsMap: IChargedPriceForFlatsItem[]
	jobs: IJobFormatted[]
	ratings: IGroupedRatings
	status: PayslipStatus
	annualCleaningBonus: number
	allYearFinishes: ICalculatedYearFinish[]

	// Complaints:
	complaints: IComplaintsSummary
	complaintsCount: number

	// Telephone surveys:
	telephoneSurveys: ITelephoneSurveysSummary
	telephoneSurveysCount: number

	errorQrCodesProblemCount: number
	errorMissingPriceCount: number
	errorMissingFlatsCount: number
	errorMissingWindowsCount: number
}

export interface IPayslipPayload {
	data: IPayslipUserDetail | null
}

export type IPayslipActions = IResetStore | {
	type: LOAD_PAYSLICES_USER
	payload: IPayslipPayload
}

export interface IFormattedTaskRating {
	id: number
	name: string
	reaction: 'LIKE' | 'DISLIKE'
}

export interface IFormattedRating {
	id: number
	rating: number
	comment: string
	createdAt: string // date as ISO string
	address: string | null
	taskRatings: IFormattedTaskRating[]
}

export interface IGroupedRatings {
	ONE_STAR: IFormattedRating[]
	TWO_STARS: IFormattedRating[]
	THREE_STARS: IFormattedRating[]
	FOUR_STARS: IFormattedRating[]
	BAD_TASKS: {
		name: string
		id: number
		count: number
	}[]
	GOOD_TASKS: {
		name: string
		id: number
		count: number
	}[]
}

export const getPayslipForUser = (
	id: number,
	month: string, // očakáva sa string vo formáte YYYY-MM alebo YYYY-MM-DD
	forceRecalculate: boolean = false,
	onSuccess: (data: IPayslipUserDetail) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<IPayslipPayload>> => async (dispatch) => {
	dispatch({
		type: LOAD_PAYSLICES_USER.START
	})

	let payload = {} as IPayslipPayload

	try {
		const { data } = await getReq(
			`/api/admin/payslips/user/${id}?month=${encodeURIComponent(month)}&forceRecalculate=${forceRecalculate}`
		)

		payload = {
			data
		}

		dispatch({
			type: LOAD_PAYSLICES_USER.DONE,
			payload
		})

		onSuccess(data)
		return payload
	} catch (error) {
		dispatch({
			type: LOAD_PAYSLICES_USER.FAILED
		})
		onFailure(error)
		return Promise.reject(error)
	}
}

export const createPayslipForUser = (
	id: number,
	body: { month: string; paycheckFromManager: number },
	onSuccess: (data: IPayslipUserDetail) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<IPayslipPayload>> => async (dispatch) => {
	dispatch({
		type: CREATE_PAYSLICES_USER.START
	})

	let payload = {} as IPayslipPayload

	try {
		const { data } = await postReq(`/api/admin/payslips/user/${id}`, null, body)

		payload = {
			data
		}

		dispatch({
			type: CREATE_PAYSLICES_USER.DONE,
			payload
		})

		onSuccess(data)
		return payload
	} catch (error) {
		dispatch({
			type: CREATE_PAYSLICES_USER.FAILED
		})
		onFailure(error)
		return Promise.reject(error)
	}
}

export const openPayslipPdf = async (payslipId: number): Promise<void> => {
	const domain = process.env.REACT_APP_API_BASE_URL || 'https://api.umyjeme.top'
	const pdfURL = `${domain}/api/admin/payslips/${payslipId}/pdf`

	// 👇 otvor nové okno okamžite (browser nezablokuje)
	const newWindow = window.open('', '_blank')
	if (!newWindow) {
		console.error('Popup blocked')
		return
	}

	try {
		const currentUser = firebaseAdmin?.auth()?.currentUser
		if (!currentUser) {
			console.error('No Firebase user')
			newWindow.close()
			return
		}

		const token = await currentUser.getIdToken(true)

		const response = await fetch(pdfURL, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${token}`
			}
		})

		if (!response.ok) {
			console.error('Failed to fetch PDF')
			newWindow.close()
			return
		}

		const blob = await response.blob()
		const blobUrl = window.URL.createObjectURL(blob)

		newWindow.location.href = blobUrl
	} catch (err) {
		console.error('Error while generating payslip PDF:', err)
		newWindow.close()
	}
}

export const finishPayslipForUser = (
	id: number,
	body: { month: string },
	onSuccess: (data: IPayslipUserDetail) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<IPayslipPayload>> => async () => {
	let payload = {} as IPayslipPayload

	try {
		const { data } = await postReq(`/api/admin/payslips/user/${id}/finish`, null, body)

		payload = {
			data
		}

		onSuccess(data)
		return payload
	} catch (error) {
		onFailure(error)
		return Promise.reject(error)
	}
}

export const resetPayslipForUser = (
	id: number,
	body: { month: string },
	onSuccess: (data: { id: number; deleted: boolean }) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<any>> => async () => {
	try {
		const { data } = await postReq(`/api/admin/payslips/user/${id}/reset`, null, body)

		onSuccess(data)
		return data
	} catch (error) {
		onFailure(error)
		return Promise.reject(error)
	}
}

